import type { MonthBucket } from '@/state/program/program-summary';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { seriesColor } from '../Experiments/Details/ExperimentStatistics/control-options';
import {
  AXIS_TICK,
  LEGEND_STYLE,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from '../Experiments/Details/ExperimentStatistics/StatisticsChart';
import { OUTCOME_FILL, OUTCOME_LABEL, OUTCOME_ORDER } from './outcome-tones';

export type VelocityChartProps = { months: MonthBucket[] };

const LAUNCHED = 'Launched';
const LAUNCHED_COLOR = seriesColor(0);

/**
 * Conclusions stacked by outcome, with launches drawn over them as a line.
 *
 * One axis, because both are counts of tests in the same month. The line is the
 * input and the bars are the output; a programme whose line rises while the green
 * share of the bars stays flat is testing more without learning more.
 *
 * Framed and furnished like the statistics screen's charts (StatisticsChart,
 * RateOverTime): the same card inside a panel, and the same axis, tooltip and legend
 * values, imported rather than copied so the two cannot drift. Bars keep the outcome
 * tokens rather than series colours, because there the colour is the meaning.
 */
export const VelocityChart = ({ months }: VelocityChartProps) => {
  // A zero is left empty rather than drawn, so the tooltip lists only the outcomes
  // that month actually had instead of four lines of "0".
  const data = months.map((bucket) => ({
    label: bucket.label,
    [LAUNCHED]: bucket.launched,
    ...Object.fromEntries(
      OUTCOME_ORDER.map((outcome) => [OUTCOME_LABEL[outcome], bucket[outcome] > 0 ? bucket[outcome] : undefined]),
    ),
  }));

  return (
    <section className="bg-panel border-line rounded-panel border p-3.5" data-tour="program-velocity">
      <div className="border-line bg-panel2 rounded-card border">
        <div className="border-line border-b px-4 py-3">
          <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">Velocity</h3>
          <p className="supporting">Tests launched per month, and how the ones that concluded turned out.</p>
        </div>

        <div className="p-4">
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                {/* Horizontal rules only, as on the statistics charts. */}
                <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={AXIS_TICK}
                  tickMargin={8}
                  minTickGap={24}
                  stroke="var(--line)"
                  tickLine={false}
                />
                <YAxis
                  width={56}
                  tick={AXIS_TICK}
                  stroke="var(--line)"
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  // A band rather than the line cursor the other charts use: the unit
                  // here is a month's bar, not a point on a line.
                  cursor={{ fill: 'hsl(var(--hover))' }}
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                />
                <Legend wrapperStyle={LEGEND_STYLE} iconType="plainline" iconSize={12} />
                {OUTCOME_ORDER.map((outcome) => (
                  <Bar
                    key={outcome}
                    dataKey={OUTCOME_LABEL[outcome]}
                    stackId="concluded"
                    fill={OUTCOME_FILL[outcome]}
                    fillOpacity={0.85}
                    maxBarSize={28}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey={LAUNCHED}
                  stroke={LAUNCHED_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, stroke: LAUNCHED_COLOR, strokeWidth: 2, fill: 'hsl(var(--panel))' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
};
